import { useEffect, useMemo, useState } from "react";
import { Check, Loader2, MoreHorizontal, Plus, Search, X } from "lucide-react";
import { Avatar, AvatarFallback } from "../../../../components/ui/avatar";
import { Button } from "../../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { useWorkspaceMembers } from "../../../../hooks/QueryHooks";
import { cn } from "../../../../lib/utils";
import {
  addTeamMember,
  createTeam,
  deleteTeam,
  listTeamMemberships,
  listTeams,
  removeTeamMember,
  updateTeam,
} from "../../../../services/teams/teamsEndpoints";
import type { Team, TeamMembership } from "../../../../services/teams/teamsEndpoints.interface";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import notify from "../../../../utils/notify";

type WorkspaceMember = {
  id?: string;
  user_id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
};

type TeamMember = {
  id: string;
  name: string;
  email: string;
  initials: string;
};

type TeamRow = {
  id: string;
  name: string;
  description: string;
  members: TeamMember[];
};

const getMemberName = (member: WorkspaceMember) => {
  const fullName = `${member.first_name ?? ""} ${member.last_name ?? ""}`.trim();
  return fullName || member.email || "Unknown Member";
};

const getInitials = (value: string) => {
  const tokens = value.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return "?";
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return `${tokens[0][0] ?? ""}${tokens[1][0] ?? ""}`.toUpperCase();
};

const toTeamMember = (member: WorkspaceMember): TeamMember | null => {
  const id = String(member.id || "").trim();
  const email = String(member.email || "").trim();
  if (!id || !email) return null;
  const name = getMemberName(member);
  return {
    id,
    name,
    email,
    initials: getInitials(name),
  };
};

const buildTempId = (prefix: string) => (
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
);

const TeamsSettingsPage = () => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const workspaceId = workspace?.id;

  const { data: membersData } = useWorkspaceMembers({
    workspaceId,
    enabled: !!workspaceId,
  });

  const [backendTeams, setBackendTeams] = useState<Team[]>([]);
  const [teamMemberships, setTeamMemberships] = useState<TeamMembership[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [saving, setSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [teamDescription, setTeamDescription] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [memberSearch, setMemberSearch] = useState("");

  const [manageOpen, setManageOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [activeTeam, setActiveTeam] = useState<TeamRow | null>(null);
  const [editTeamName, setEditTeamName] = useState("");
  const [editTeamDescription, setEditTeamDescription] = useState("");
  const [editSelectedMembers, setEditSelectedMembers] = useState<Set<string>>(new Set());
  const [editMemberSearch, setEditMemberSearch] = useState("");

  const workspaceMembers = useMemo<TeamMember[]>(() => {
    const rows = ((membersData?.members ?? membersData?.users ?? []) as WorkspaceMember[])
      .map((member) => toTeamMember(member))
      .filter((member): member is TeamMember => !!member);

    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }, [membersData?.members, membersData?.users]);

  const workspaceMemberById = useMemo(() => {
    const map = new Map<string, TeamMember>();
    workspaceMembers.forEach((member) => map.set(member.id, member));
    return map;
  }, [workspaceMembers]);

  const teams = useMemo<TeamRow[]>(() => (
    backendTeams.map((team) => {
      const members = teamMemberships
        .filter((membership) => membership.team_id === team.id && membership.is_active)
        .map((membership) => {
          const mapped = workspaceMemberById.get(membership.workspace_member_id);
          if (mapped) return mapped;
          return {
            id: membership.workspace_member_id,
            name: "Unknown Member",
            email: "",
            initials: "??",
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      return {
        id: team.id,
        name: team.name,
        description: team.description || "",
        members,
      };
    })
  ), [backendTeams, teamMemberships, workspaceMemberById]);

  const loadTeamsData = async () => {
    if (!workspaceId) {
      setBackendTeams([]);
      setTeamMemberships([]);
      return;
    }

    setLoadingTeams(true);
    try {
      const [teamsResponse, membershipsResponse] = await Promise.all([
        listTeams({ workspace_id: workspaceId }),
        listTeamMemberships({ workspace_id: workspaceId }),
      ]);

      setBackendTeams(teamsResponse.data || []);
      setTeamMemberships(membershipsResponse.data || []);
    } catch {
      notify({
        type: "failure",
        heading: "Unable to load teams",
        messageList: ["Please try again later."],
      });
      setBackendTeams([]);
      setTeamMemberships([]);
    } finally {
      setLoadingTeams(false);
    }
  };

  useEffect(() => {
    loadTeamsData().catch(() => {});
  }, [workspaceId]);

  const filteredMembers = useMemo(() => {
    if (!memberSearch.trim()) return workspaceMembers;
    const query = memberSearch.trim().toLowerCase();
    return workspaceMembers.filter((member) => (
      member.name.toLowerCase().includes(query)
      || member.email.toLowerCase().includes(query)
    ));
  }, [memberSearch, workspaceMembers]);

  const filteredEditMembers = useMemo(() => {
    if (!editMemberSearch.trim()) return workspaceMembers;
    const query = editMemberSearch.trim().toLowerCase();
    return workspaceMembers.filter((member) => (
      member.name.toLowerCase().includes(query)
      || member.email.toLowerCase().includes(query)
    ));
  }, [editMemberSearch, workspaceMembers]);

  const resetForm = () => {
    setTeamName("");
    setTeamDescription("");
    setSelectedMembers(new Set());
    setMemberSearch("");
  };

  const handleDialogChange = (open: boolean) => {
    setCreateOpen(open);
    if (!open) resetForm();
  };

  const toggleMember = (id: string) => {
    setSelectedMembers((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleEditMember = (id: string) => {
    setEditSelectedMembers((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreate = async () => {
    if (!workspaceId || !teamName.trim()) return;

    const previousTeams = [...backendTeams];
    const previousMemberships = [...teamMemberships];
    const trimmedName = teamName.trim();
    const trimmedDescription = teamDescription.trim();
    const optimisticTeamId = buildTempId("team");
    const optimisticMemberIds = Array.from(selectedMembers);
    const optimisticTeam: Team = {
      id: optimisticTeamId,
      workspace_id: workspaceId,
      name: trimmedName,
      description: trimmedDescription || null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    const optimisticMemberships: TeamMembership[] = optimisticMemberIds.map((workspaceMemberId) => ({
      id: buildTempId("team_member"),
      team_id: optimisticTeamId,
      workspace_member_id: workspaceMemberId,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }));

    setBackendTeams((previous) => [optimisticTeam, ...previous]);
    setTeamMemberships((previous) => [...previous, ...optimisticMemberships]);

    setSaving(true);
    try {
      const createdTeamResponse = await createTeam({
        workspace_id: workspaceId,
        name: trimmedName,
        description: trimmedDescription || undefined,
      });

      const createdTeam = createdTeamResponse.data;
      if (!createdTeam?.id) {
        throw new Error("Team creation did not return an id");
      }

      setBackendTeams((previous) => previous.map((team) => (
        team.id === optimisticTeamId ? createdTeam : team
      )));
      setTeamMemberships((previous) => previous.map((membership) => (
        membership.team_id === optimisticTeamId
          ? { ...membership, team_id: createdTeam.id }
          : membership
      )));

      if (optimisticMemberIds.length > 0) {
        const membershipResults = await Promise.allSettled(
          optimisticMemberIds.map((workspaceMemberId) => addTeamMember({
            workspace_id: workspaceId,
            team_id: createdTeam.id,
            workspace_member_id: workspaceMemberId,
          })),
        );

        const failedCount = membershipResults.filter((result) => result.status === "rejected").length;
        if (failedCount > 0) {
          await loadTeamsData();
          notify({
            type: "failure",
            heading: "Some members were not added",
            messageList: [`${failedCount} member assignment${failedCount > 1 ? "s" : ""} failed.`],
          });
        } else {
          loadTeamsData().catch(() => {});
        }
      } else {
        loadTeamsData().catch(() => {});
      }

      handleDialogChange(false);
      notify({ type: "success", messageList: ["Team created."] });
    } catch {
      setBackendTeams(previousTeams);
      setTeamMemberships(previousMemberships);
      notify({
        type: "failure",
        heading: "Unable to create team",
        messageList: ["Please try again later."],
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeam = (team: TeamRow) => {
    setActiveTeam(team);
    setDeleteOpen(true);
  };

  const openManageTeam = (team: TeamRow) => {
    const currentMemberIds = teamMemberships
      .filter((membership) => membership.team_id === team.id && membership.is_active)
      .map((membership) => membership.workspace_member_id);

    setActiveTeam(team);
    setEditTeamName(team.name);
    setEditTeamDescription(team.description || "");
    setEditSelectedMembers(new Set(currentMemberIds));
    setEditMemberSearch("");
    setManageOpen(true);
  };

  const closeManageDialog = (open: boolean) => {
    setManageOpen(open);
    if (!open) {
      setEditTeamName("");
      setEditTeamDescription("");
      setEditSelectedMembers(new Set());
      setEditMemberSearch("");
      if (!deleteOpen) setActiveTeam(null);
    }
  };

  const closeDeleteDialog = (open: boolean) => {
    setDeleteOpen(open);
    if (!open && !manageOpen) {
      setActiveTeam(null);
    }
  };

  const handleSaveTeamChanges = async () => {
    if (!workspaceId || !activeTeam || !editTeamName.trim()) return;

    const previousTeams = [...backendTeams];
    const previousMemberships = [...teamMemberships];
    const trimmedName = editTeamName.trim();
    const trimmedDescription = editTeamDescription.trim();
    const existingMemberships = teamMemberships.filter((membership) => (
      membership.team_id === activeTeam.id && membership.is_active
    ));
    const existingMemberIds = new Set(existingMemberships.map((membership) => membership.workspace_member_id));
    const targetMemberIds = new Set(editSelectedMembers);
    const toAdd = Array.from(targetMemberIds).filter((workspaceMemberId) => !existingMemberIds.has(workspaceMemberId));
    const toRemove = existingMemberships.filter((membership) => !targetMemberIds.has(membership.workspace_member_id));
    const optimisticMembershipsToAdd: TeamMembership[] = toAdd.map((workspaceMemberId) => ({
      id: buildTempId("team_member"),
      team_id: activeTeam.id,
      workspace_member_id: workspaceMemberId,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }));

    setBackendTeams((previous) => previous.map((team) => (
      team.id === activeTeam.id
        ? { ...team, name: trimmedName, description: trimmedDescription || null }
        : team
    )));
    setTeamMemberships((previous) => [
      ...previous.filter((membership) => !toRemove.some((removed) => removed.id === membership.id)),
      ...optimisticMembershipsToAdd,
    ]);

    setSaving(true);
    try {
      await updateTeam({
        workspace_id: workspaceId,
        team_id: activeTeam.id,
        name: trimmedName,
        description: trimmedDescription || undefined,
      });

      const membershipResults = await Promise.allSettled([
        ...toAdd.map((workspaceMemberId) => addTeamMember({
          workspace_id: workspaceId,
          team_id: activeTeam.id,
          workspace_member_id: workspaceMemberId,
        })),
        ...toRemove.map((membership) => removeTeamMember({
          workspace_id: workspaceId,
          team_id: activeTeam.id,
          membership_id: membership.id,
        })),
      ]);

      const failedCount = membershipResults.filter((result) => result.status === "rejected").length;
      if (failedCount > 0) {
        await loadTeamsData();
        closeManageDialog(false);
        notify({
          type: "failure",
          heading: "Some changes failed",
          messageList: [`${failedCount} member update${failedCount > 1 ? "s" : ""} could not be saved.`],
        });
        return;
      }

      loadTeamsData().catch(() => {});
      closeManageDialog(false);
      notify({ type: "success", messageList: ["Team updated."] });
    } catch {
      setBackendTeams(previousTeams);
      setTeamMemberships(previousMemberships);
      notify({
        type: "failure",
        heading: "Unable to update team",
        messageList: ["Please try again later."],
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeamConfirm = async () => {
    if (!workspaceId || !activeTeam) return;

    const previousTeams = [...backendTeams];
    const previousMemberships = [...teamMemberships];
    const deletingTeamId = activeTeam.id;

    setBackendTeams((previous) => previous.filter((team) => team.id !== deletingTeamId));
    setTeamMemberships((previous) => previous.filter((membership) => membership.team_id !== deletingTeamId));
    closeDeleteDialog(false);

    setSaving(true);
    try {
      await deleteTeam({ workspace_id: workspaceId, team_id: deletingTeamId });
      loadTeamsData().catch(() => {});
      notify({ type: "success", messageList: ["Team deleted."] });
    } catch {
      setBackendTeams(previousTeams);
      setTeamMemberships(previousMemberships);
      notify({
        type: "failure",
        heading: "Unable to delete team",
        messageList: ["Please try again later."],
      });
    } finally {
      setSaving(false);
    }
  };

  const isBusy = saving || loadingTeams;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[15px] font-semibold text-foreground">Teams</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">Organize members into functional teams</p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)} className="h-8 gap-1.5 text-[12px]" disabled={!workspaceId || isBusy}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          Create Team
        </Button>
      </div>

      {loadingTeams ? (
        <div className="rounded-lg border border-border py-12 text-center">
          <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
          <p className="mt-3 text-[13px] text-muted-foreground">Loading teams...</p>
        </div>
      ) : null}

      {!loadingTeams ? (
        <div className="overflow-hidden rounded-lg border border-border">
          {teams.map((team, index) => (
            <div
              key={team.id}
              className={cn(
                "group flex items-center justify-between px-4 py-3.5 transition-colors hover:bg-muted/50",
                index < teams.length - 1 && "border-b border-border",
              )}
            >
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium text-foreground">{team.name}</p>
                <p className="mt-0.5 text-[12px] text-muted-foreground">{team.description || "No description"}</p>
              </div>
              <div className="flex flex-shrink-0 items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-1.5">
                    {team.members.slice(0, 4).map((member) => (
                      <Avatar key={`${team.id}-${member.id}`} className="h-6 w-6 ring-2 ring-background">
                        <AvatarFallback className="bg-muted text-[9px] font-medium text-muted-foreground">
                          {member.initials}
                        </AvatarFallback>
                      </Avatar>
                    ))}
                    {team.members.length > 4 ? (
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[9px] font-medium text-muted-foreground ring-2 ring-background">
                        +{team.members.length - 4}
                      </div>
                    ) : null}
                  </div>
                  <span className="text-[11px] text-muted-foreground">{team.members.length}</span>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex h-7 w-7 items-center justify-center rounded opacity-0 transition-opacity hover:bg-muted group-hover:opacity-100"
                      disabled={isBusy}
                      title="Team actions"
                    >
                      <MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-36">
                    <DropdownMenuItem onClick={() => openManageTeam(team)}>
                      Manage team
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDeleteTeam(team)}
                      className="text-status-cancelled focus:text-status-cancelled"
                    >
                      Delete team
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {!loadingTeams && teams.length === 0 ? (
        <div className="rounded-lg border border-border py-12 text-center">
          <p className="text-[13px] text-muted-foreground">No teams yet. Create one to get started.</p>
        </div>
      ) : null}

      <Dialog open={createOpen} onOpenChange={handleDialogChange}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="text-[15px] font-semibold">Create Team</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="space-y-1.5">
              <Label className="text-[13px]">Team Name</Label>
              <Input
                placeholder="e.g. Sales Team"
                value={teamName}
                onChange={(event) => setTeamName(event.target.value)}
                className="h-9 text-[13px]"
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[13px]">
                Description <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Input
                placeholder="What does this team do?"
                value={teamDescription}
                onChange={(event) => setTeamDescription(event.target.value)}
                className="h-9 text-[13px]"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[13px]">Members</Label>
                {selectedMembers.size > 0 ? (
                  <span className="text-[11px] text-muted-foreground">{selectedMembers.size} selected</span>
                ) : null}
              </div>

              {selectedMembers.size > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {workspaceMembers
                    .filter((member) => selectedMembers.has(member.id))
                    .map((member) => (
                      <button
                        key={member.id}
                        onClick={() => toggleMember(member.id)}
                        className="flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1 text-[12px] font-medium text-primary transition-colors hover:bg-primary/15"
                      >
                        {member.name}
                        <X className="h-3 w-3" />
                      </button>
                    ))}
                </div>
              ) : null}

              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search workspace members..."
                  value={memberSearch}
                  onChange={(event) => setMemberSearch(event.target.value)}
                  className="h-8 pl-8 text-[13px]"
                />
              </div>

              <div className="max-h-[200px] overflow-y-auto rounded-lg border border-border">
                {filteredMembers.map((member) => {
                  const isSelected = selectedMembers.has(member.id);
                  return (
                    <button
                      key={member.id}
                      onClick={() => toggleMember(member.id)}
                      className={cn(
                        "flex w-full items-center gap-3 border-b border-border px-3 py-2.5 text-left transition-colors last:border-b-0",
                        isSelected ? "bg-primary/[0.04]" : "hover:bg-muted/50",
                      )}
                    >
                      <Avatar className="h-7 w-7 flex-shrink-0">
                        <AvatarFallback className="bg-muted text-[10px] font-medium text-muted-foreground">
                          {member.initials}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-medium text-foreground">{member.name}</p>
                        <p className="truncate text-[11px] text-muted-foreground">{member.email}</p>
                      </div>
                      <div
                        className={cn(
                          "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-colors",
                          isSelected ? "border-primary bg-primary" : "border-border",
                        )}
                      >
                        {isSelected ? <Check className="h-3 w-3 text-primary-foreground" /> : null}
                      </div>
                    </button>
                  );
                })}

                {filteredMembers.length === 0 ? (
                  <div className="px-3 py-3 text-[12px] text-muted-foreground">No members found.</div>
                ) : null}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-end">
            <Button variant="outline" size="sm" onClick={() => handleDialogChange(false)} className="h-8 text-[12px]">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => {
                handleCreate().catch(() => {});
              }}
              disabled={!teamName.trim() || isBusy || !workspaceId}
              className="h-8 text-[12px]"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Create Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={manageOpen} onOpenChange={closeManageDialog}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="text-[15px] font-semibold">Manage Team</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="space-y-1.5">
              <Label className="text-[13px]">Team Name</Label>
              <Input
                placeholder="e.g. Sales Team"
                value={editTeamName}
                onChange={(event) => setEditTeamName(event.target.value)}
                className="h-9 text-[13px]"
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[13px]">
                Description <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Input
                placeholder="What does this team do?"
                value={editTeamDescription}
                onChange={(event) => setEditTeamDescription(event.target.value)}
                className="h-9 text-[13px]"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[13px]">Members</Label>
                {editSelectedMembers.size > 0 ? (
                  <span className="text-[11px] text-muted-foreground">{editSelectedMembers.size} selected</span>
                ) : null}
              </div>

              {editSelectedMembers.size > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {workspaceMembers
                    .filter((member) => editSelectedMembers.has(member.id))
                    .map((member) => (
                      <button
                        key={member.id}
                        onClick={() => toggleEditMember(member.id)}
                        className="flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1 text-[12px] font-medium text-primary transition-colors hover:bg-primary/15"
                      >
                        {member.name}
                        <X className="h-3 w-3" />
                      </button>
                    ))}
                </div>
              ) : null}

              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search workspace members..."
                  value={editMemberSearch}
                  onChange={(event) => setEditMemberSearch(event.target.value)}
                  className="h-8 pl-8 text-[13px]"
                />
              </div>

              <div className="max-h-[200px] overflow-y-auto rounded-lg border border-border">
                {filteredEditMembers.map((member) => {
                  const isSelected = editSelectedMembers.has(member.id);
                  return (
                    <button
                      key={member.id}
                      onClick={() => toggleEditMember(member.id)}
                      className={cn(
                        "flex w-full items-center gap-3 border-b border-border px-3 py-2.5 text-left transition-colors last:border-b-0",
                        isSelected ? "bg-primary/[0.04]" : "hover:bg-muted/50",
                      )}
                    >
                      <Avatar className="h-7 w-7 flex-shrink-0">
                        <AvatarFallback className="bg-muted text-[10px] font-medium text-muted-foreground">
                          {member.initials}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-medium text-foreground">{member.name}</p>
                        <p className="truncate text-[11px] text-muted-foreground">{member.email}</p>
                      </div>
                      <div
                        className={cn(
                          "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-colors",
                          isSelected ? "border-primary bg-primary" : "border-border",
                        )}
                      >
                        {isSelected ? <Check className="h-3 w-3 text-primary-foreground" /> : null}
                      </div>
                    </button>
                  );
                })}

                {filteredEditMembers.length === 0 ? (
                  <div className="px-3 py-3 text-[12px] text-muted-foreground">No members found.</div>
                ) : null}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-end">
            <Button variant="outline" size="sm" onClick={() => closeManageDialog(false)} className="h-8 text-[12px]">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => {
                handleSaveTeamChanges().catch(() => {});
              }}
              disabled={!editTeamName.trim() || isBusy || !workspaceId}
              className="h-8 text-[12px]"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={closeDeleteDialog}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="text-[15px] font-semibold">Delete Team</DialogTitle>
          </DialogHeader>

          <p className="text-[13px] text-muted-foreground">
            Are you sure you want to delete {activeTeam?.name ? `"${activeTeam.name}"` : "this team"}?
          </p>

          <DialogFooter className="gap-2 sm:justify-end">
            <Button variant="outline" size="sm" onClick={() => closeDeleteDialog(false)} className="h-8 text-[12px]">
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                handleDeleteTeamConfirm().catch(() => {});
              }}
              disabled={isBusy}
              className="h-8 text-[12px]"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Delete Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamsSettingsPage;

